<script setup lang="ts">
import { ref } from 'vue'
import {
  PAvatar,
  PAvatarSize,
  PAvatarType,
  PCard,
  PCardRound,
  PCardSize,
  PContextMenu,
  PIcon,
  PItem,
  PList,
  PMaxLines,
  PSpace,
  PSpaceSize,
  PTextDescription,
  PTextHighlighted,
} from '@previo/design-system'
import { $t } from '@previo/core'
import { Color } from '@previo/codebooks'
import { useLogout } from '../composables/useLogout'
import UserOptionsList from './UserOptionsList.vue'
import { useUserStore } from '~/store/user'

type Emits = {
  (e: 'navigate'): void
}

const emit = defineEmits<Emits>()

const userStore = useUserStore()
const { isDoingLogout, logout } = useLogout()
const userOptionsMenuVisible = ref<boolean>(false)

const onNavigate = (): void => {
  userOptionsMenuVisible.value = false
  emit('navigate')
}
</script>

<template>
  <p-card
    class="p-flex p-items-center p-no-wrap p-cursor-pointer"
    :round="PCardRound.MD"
    :pa-mobile="PCardSize.SM"
    show-border
    data-cy="side-menu-user"
    @click="userOptionsMenuVisible = !userOptionsMenuVisible"
  >
    <p-avatar
      :type="PAvatarType.MONOGRAM"
      :text="userStore.user.name"
      :size="PAvatarSize.LARGE"
      class="p-card-avoid-border"
    />
    <p-space :mobile="PSpaceSize.SM" />

    <div class="p-col">
      <p-text-highlighted :text="userStore.user.name" :max-lines="PMaxLines.ONE" />
      <p-text-description
        :text="userStore.user.login"
        :color="Color.GREY_500"
        :max-lines="PMaxLines.ONE"
      />
    </div>

    <p-space :mobile="PSpaceSize.SM" />
    <p-icon icon="chevron-down" :color="Color.GREY_500" />

    <p-context-menu v-model="userOptionsMenuVisible" :title="userStore.user.name" cy="user-options">
      <p-list>
        <user-options-list has-icons @navigate="onNavigate" />

        <p-item
          :text="$t('Log out')"
          icon-start="arrow-right-from-bracket"
          :color="Color.NEGATIVE"
          :loading="isDoingLogout"
          cy="logout"
          @click="logout"
        />
      </p-list>
    </p-context-menu>
  </p-card>
</template>
